import { useCallback, useEffect, useRef, useState } from 'react'
import { useInterval } from './useInterval'
import type { Feed, Photo, SpriteSheet } from './AevocamViewer'

/** CDN / manifest pipeline: sprites, photos array, token, viewer box size */
export type AevocamAssetState = {
  /** 0 = before summary, 1 = summary ok, 2 = manifest ok */
  pipelineStage: number
  photos: (Photo | undefined)[]
  spriteSheetsLow: SpriteSheet[]
  spriteSheetsMedium: SpriteSheet[]
  spritesHighGearLow: boolean
  spritesHighGearMedium: boolean
  viewWidth: number
  viewHeight: number
  cdnToken: string | undefined
  /** CDN token TTL (seconds), used for refresh interval */
  cdnTokenTtlSec: number
}

export type AevocamAssetDispatchers = {
  setSpritesHighGearLow: (value: boolean | ((prev: boolean) => boolean)) => void
  setSpritesHighGearMedium: (value: boolean | ((prev: boolean) => boolean)) => void
}

type FeedSummaryResponse = {
  data: {
    feed: Feed
    totalPhotos: number
    firstPhoto: Photo
    lastPhoto: Photo
    spriteSheetsLow: SpriteSheet[]
  }
}

type FeedManifestResponse = {
  data: {
    spriteSheetsMedium: SpriteSheet[]
    photos: Photo[]
  }
}

export type UseAevocamAssetStateParams = {
  startAtLatest: boolean
  sessionKey: string | undefined
  cdnUrl: string
  apiUrl: string
  tenantId: string
  feedId: string
  /** Optional hour filter (HHMMSS string, e.g. "130000") passed to manifest query param. */
  timeSlot: string | undefined
  /** Latest layout “viewer width” from chrome (for initial box sizing) */
  layoutViewerWidth: number | undefined
  currentIndex: number
  reportSummaryLoaded: (feed: Feed, totalPhotos: number) => void
  reportManifestLoaded: () => void
  reportCurrentPhoto: (photo: Photo | undefined) => void
}

export function useAevocamAssetState({
  startAtLatest,
  sessionKey,
  cdnUrl,
  apiUrl,
  tenantId,
  feedId,
  timeSlot,
  layoutViewerWidth,
  currentIndex,
  reportSummaryLoaded,
  reportManifestLoaded,
  reportCurrentPhoto,
}: UseAevocamAssetStateParams): AevocamAssetState & AevocamAssetDispatchers {
  const [assets, setAssets] = useState<AevocamAssetState>(() => ({
    pipelineStage: 0,
    photos: [],
    spriteSheetsLow: [],
    spriteSheetsMedium: [],
    spritesHighGearLow: true,
    spritesHighGearMedium: false,
    viewWidth: 400,
    viewHeight: 400,
    cdnToken: undefined,
    cdnTokenTtlSec: 3600,
  }))

  const layoutViewerWidthRef = useRef(layoutViewerWidth)
  layoutViewerWidthRef.current = layoutViewerWidth

  // Reset the asset pipeline when the identity/filter changes.
  useEffect(() => {
    setAssets(a => {
      if (
        a.pipelineStage === 0 &&
        a.photos.length === 0 &&
        a.spriteSheetsLow.length === 0 &&
        a.spriteSheetsMedium.length === 0
      ) {
        return a
      }
      return {
        ...a,
        pipelineStage: 0,
        photos: [],
        spriteSheetsLow: [],
        spriteSheetsMedium: [],
        spritesHighGearLow: true,
        spritesHighGearMedium: false,
        viewWidth: 400,
        viewHeight: 400,
      }
    })
  }, [tenantId, feedId, timeSlot])

  const {
    pipelineStage,
    photos,
    spriteSheetsLow,
    spriteSheetsMedium,
    spritesHighGearLow,
    spritesHighGearMedium,
    viewWidth,
    viewHeight,
    cdnToken,
    cdnTokenTtlSec,
  } = assets

  //------------------------------------------------------------
  const fetchToken = useCallback(async () => {
    try {
      const url = `${apiUrl}/t/${tenantId}/feed/${feedId}/token`
      const res = await fetch(
        url,
        sessionKey ? { headers: { Authorization: `Bearer ${sessionKey}` } } : {},
      )
      const json = res.ok
        ? await res.json().catch(() => res.text())
        : await res.text().catch(() => null)
      setAssets(a => ({
        ...a,
        cdnToken: json?.data?.token,
        cdnTokenTtlSec: json?.data?.ttl ?? a.cdnTokenTtlSec,
      }))
    } catch (err) {
      console.error('feed token error', err)
    }
  }, [apiUrl, tenantId, feedId, sessionKey])

  // TODO: make interval sensitive to returned expiration
  useInterval(fetchToken, (cdnTokenTtlSec - 60) * 1000)
  useEffect(() => {
    fetchToken()
  }, [fetchToken])

  //------------------------------------------------------------
  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const summaryUrl = `${apiUrl}/t/${tenantId}/feed/${feedId}`
        const summaryResponse = await fetch(
          summaryUrl,
          sessionKey ? { headers: { Authorization: `Bearer ${sessionKey}` } } : {},
        )
        if (!summaryResponse.ok) {
          throw new Error('Failed to fetch feed summary')
        }
        const summaryData: FeedSummaryResponse = await summaryResponse.json()

        setAssets(a => {
          const photosArray: (Photo | undefined)[] = new Array(summaryData.data.totalPhotos).fill(undefined)
          photosArray[0] = summaryData.data.firstPhoto
          if (summaryData.data.totalPhotos > 1) {
            photosArray[summaryData.data.totalPhotos - 1] = summaryData.data.lastPhoto
          }
          const initialPhoto = startAtLatest ? summaryData.data.lastPhoto : summaryData.data.firstPhoto
          const w = layoutViewerWidthRef.current
          const calculatedWidth = w !== undefined ? w : Math.max(400, initialPhoto?.w ?? 100)
          const calculatedHeight = Math.round(calculatedWidth * ((initialPhoto?.h ?? 100) / (initialPhoto?.w ?? 100)))
          return {
            ...a,
            photos: photosArray,
            spriteSheetsLow: summaryData.data.spriteSheetsLow || [],
            viewWidth: calculatedWidth,
            viewHeight: calculatedHeight,
            pipelineStage: 1,
          }
        })

        reportSummaryLoaded(summaryData.data.feed, summaryData.data.totalPhotos)

        const manifestUrl = `${apiUrl}/t/${tenantId}/feed/${feedId}/manifest`
        const manifestUrlWithQuery = (() => {
          if (!timeSlot) return manifestUrl
          const u = new URL(manifestUrl)
          u.searchParams.set('timeSlot', timeSlot)
          return u.toString()
        })()
        const manifestResponse = await fetch(
          manifestUrlWithQuery,
          sessionKey ? { headers: { Authorization: `Bearer ${sessionKey}` } } : {},
        )
        if (!manifestResponse.ok) {
          throw new Error('Failed to fetch feed manifest')
        }
        const manifestData: FeedManifestResponse = await manifestResponse.json()

        setAssets(a => ({
          ...a,
          photos: manifestData.data.photos || [],
          spriteSheetsMedium: manifestData.data.spriteSheetsMedium || [],
          pipelineStage: 2,
        }))

        reportManifestLoaded()
      } catch (error) {
        console.error('Error fetching feed:', error)
      }
    }

    fetchFeed()
  }, [
    tenantId,
    feedId,
    startAtLatest,
    apiUrl,
    sessionKey,
    cdnUrl,
    timeSlot,
    reportSummaryLoaded,
    reportManifestLoaded,
  ])

  const currentPhotoFromAssets = photos[currentIndex]

  useEffect(() => {
    reportCurrentPhoto(currentPhotoFromAssets)
  }, [currentPhotoFromAssets, reportCurrentPhoto])

  useEffect(() => {
    if (pipelineStage >= 2 && currentPhotoFromAssets && layoutViewerWidth === undefined) {
      const calculatedWidth = Math.max(400, currentPhotoFromAssets.w)
      const calculatedHeight = Math.round(calculatedWidth * (currentPhotoFromAssets.h / currentPhotoFromAssets.w))
      setAssets(a => {
        if (a.viewWidth === calculatedWidth && a.viewHeight === calculatedHeight) return a
        return { ...a, viewWidth: calculatedWidth, viewHeight: calculatedHeight }
      })
    }
  }, [pipelineStage, currentPhotoFromAssets, layoutViewerWidth])

  useEffect(() => {
    if (layoutViewerWidth !== undefined && layoutViewerWidth > 0 && currentPhotoFromAssets) {
      const calculatedHeight = Math.round(
        layoutViewerWidth * (currentPhotoFromAssets.h / currentPhotoFromAssets.w),
      )
      setAssets(a => {
        if (a.viewWidth === layoutViewerWidth && a.viewHeight === calculatedHeight) return a
        return { ...a, viewWidth: layoutViewerWidth, viewHeight: calculatedHeight }
      })
    }
  }, [layoutViewerWidth, currentPhotoFromAssets])

  const setSpritesHighGearLow = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    setAssets(a => ({
      ...a,
      spritesHighGearLow: typeof value === 'function' ? value(a.spritesHighGearLow) : value,
    }))
  }, [])

  const setSpritesHighGearMedium = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    setAssets(a => ({
      ...a,
      spritesHighGearMedium: typeof value === 'function' ? value(a.spritesHighGearMedium) : value,
    }))
  }, [])

  return {
    pipelineStage,
    photos,
    spriteSheetsLow,
    spriteSheetsMedium,
    spritesHighGearLow,
    spritesHighGearMedium,
    viewWidth,
    viewHeight,
    cdnToken,
    cdnTokenTtlSec,
    setSpritesHighGearLow,
    setSpritesHighGearMedium,
  }
}
